import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import i18n from '../i18n';
import { errorMessage } from '../errors';
import { initials } from '../shell/collections';
import { Alert, Button, Dialog, IconButton, Pill, Select, Skeleton, TextField } from '../ui';
import { TrashIcon } from '../ui/icons';

/** Who sees a collection: add someone by e-mail (read or edit), change or remove a share. */
function ShareModal({ inventory, onClose }) {
  const { t } = useTranslation();
  const [shares, setShares] = useState(null);   // null while loading
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('read');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const base = `/inventories/${inventory.id}/shares`;
  useEffect(() => {
    api.get(base)
      .then((r) => setShares(r.data))
      .catch((err) => { setShares([]); setError(errorMessage(i18n.t, err, 'share.loadError')); });
  }, [base]);

  // Returns true when the request succeeded.
  const run = async (request) => {
    setError('');
    try {
      const r = await request();
      if (Array.isArray(r.data)) setShares(r.data);
      else setShares((await api.get(base)).data);
      return true;
    } catch (err) {
      setError(errorMessage(t, err, 'common.actionFailed'));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await run(() => api.post(base, { email, permission }))) setEmail('');
    setBusy(false);
  };

  const permissionOptions = (
    <>
      <option value="read">{t('share.canView')}</option>
      <option value="edit">{t('share.canEdit')}</option>
    </>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('share.title', { name: inventory.name })}
      description={t('share.intro')}
      footer={<Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className="dialog-form">
        {error && <Alert tone="danger">{error}</Alert>}
        <form onSubmit={add} className="share-add">
          <TextField type="email" label={t('share.emailLabel')} placeholder={t('share.emailPlaceholder')} required
                     value={email} onChange={(e) => setEmail(e.target.value)} className="share-add__email" />
          <Select label={t('share.permission')} value={permission} onChange={(e) => setPermission(e.target.value)} className="share-add__permission">
            {permissionOptions}
          </Select>
          <Button type="submit" loading={busy} className="share-add__submit">{t('share.submit')}</Button>
        </form>

        <section aria-labelledby="share-people">
          <h3 id="share-people" className="dialog-section-title">{t('share.people')}</h3>
          {shares === null ? (
            <Skeleton height={56} />
          ) : shares.length === 0 ? (
            <p className="ui-field__hint">{t('share.none')}</p>
          ) : (
            <ul className="people-list">
              {shares.map((s) => (
                <li key={s.user_id} className="people-list__row">
                  <span className="shell-account__avatar" aria-hidden="true">{initials(s.display_name || s.email)}</span>
                  <span className="people-list__who">
                    <span className="people-list__name">
                      {s.display_name}
                      {['disabled', 'pending', 'rejected'].includes(s.status) && (
                        <Pill tone="danger" title={t(`accounts.status.${s.status}`)}>{t('share.inactive')}</Pill>
                      )}
                    </span>
                    <span className="people-list__email">{s.email}</span>
                  </span>
                  <Select label={t('share.permissionOf', { name: s.display_name })} hideLabel value={s.permission}
                          className="people-list__permission"
                          onChange={(e) => run(() => api.put(`${base}/${s.user_id}`, { permission: e.target.value }))}>
                    {permissionOptions}
                  </Select>
                  <IconButton icon={TrashIcon} variant="ghost" className="people-list__remove"
                              label={t('share.removeNamed', { name: s.display_name })}
                              onClick={() => run(() => api.delete(`${base}/${s.user_id}`))} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Dialog>
  );
}

export default ShareModal;
